import type { GraphQLError } from 'graphql';
import type { ExecutionResult, Sink, SubscribePayload } from 'graphql-ws';
import type { GraphQLResponse, RequestParameters, SubscribeFunction, Variables } from 'relay-runtime';
import { Observable } from 'relay-runtime';
import { getAnalyticsWebClientPromise } from '../../analytics/client';
import { getClient } from '../subscription-client/subscription-client';

export const subscribeOptions = {
    extensions: {
        ...(typeof process.env.AGG_BRANCH_SLUG === 'string' && process.env.AGG_BRANCH_SLUG !== ''
            ? { schema: process.env.AGG_BRANCH_SLUG }
            : {}),
    },
    onTrigger: (payload: SubscribePayload) =>
        getAnalyticsWebClientPromise().then((analyticsClient) => {
            const analyticsPayload = {
                action: 'triggered',
                actionSubject: 'relaySubscription',
                source: 'portal-frontend',
                ...payload,
            };
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            analyticsClient.getInstance().sendOperationalEvent(analyticsPayload);
        }),
    onError: (payload: { operationName?: string | null; requestId: string | null }) =>
        getAnalyticsWebClientPromise().then((analyticsClient) => {
            const analyticsPayload = {
                action: 'failed',
                actionSubject: 'relaySubscription',
                source: 'portal-frontend',
                operationName: payload.operationName,
                requestId: payload.requestId,
            };
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            analyticsClient.getInstance().sendOperationalEvent(analyticsPayload);
        }),
};

/*
    This method is used to handle the subscriptions in relay network layer.
    ref: https://relay.dev/docs/guided-tour/updating-data/graphql-subscriptions/
*/
export const subscribe: SubscribeFunction = ({ id, name: operationName }: RequestParameters, variables: Variables) =>
    Observable.create((sink) => {
        const client = getClient();
        if (!client) {
            return sink.error(new Error('Unable to create subscription client'));
        }
        const payload: SubscribePayload = {
            operationName,
            variables,
            query: '',
            extensions: {
                pq: id,
                ...subscribeOptions.extensions,
            },
        };
        let requestId: string | null;

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        subscribeOptions.onTrigger(payload);

        return client.subscribe(payload, {
            ...sink,
            next: (value: { extensions?: { gateway?: { request_id?: string } } }) => {
                const aggRequestId = value?.extensions?.gateway?.request_id;
                requestId = aggRequestId ?? requestId;
                sink.next(value as GraphQLResponse);
            },
            error: (sinkError: SinkError) => {
                const errorInfo = getSinkError(sinkError);
                sink.error(errorInfo);
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                subscribeOptions.onError({ operationName, requestId });
            },
        } as Sink<ExecutionResult<Record<string, unknown>, unknown>>);
    });
export type SinkError = Error | readonly GraphQLError[] | CloseEvent;
export const getSinkError = (error: SinkError): Error => {
    let errorInfo: Error;
    if (Array.isArray(error)) {
        [errorInfo] = error as readonly GraphQLError[];
    } else if (error instanceof Error) {
        errorInfo = error;
    } else if (typeof error === 'object' && 'type' in error && error.type === 'close') {
        errorInfo = new Error(`CloseEvent: ${(error as CloseEvent).reason}`);
    } else if (error instanceof Event) {
        errorInfo = new Error(`Event: ${(error as Event).type}`);
    } else {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        errorInfo = new Error(`Unknown sink error: ${error}`);
    }
    return errorInfo;
};
